import type { PoActivityEvent } from '@/types/poActivity';

export const PO_STATUS = { LATE: 'late', SHIPPED: 'shipped', NOT_PLANNED: 'not_planned', PARTLY_PLANNED: 'partly_planned', PLANNED: 'planned' } as const;
export type PoStatus = (typeof PO_STATUS)[keyof typeof PO_STATUS];

export const ATTENTION = {
  DELIVERY_REQUESTED: 'delivery_requested',
  PLAN_EXCEEDS_ORDER: 'plan_exceeds_order',
  PACKING_LIST_MISSING: 'packing_list_missing',
  OVERDUE: 'overdue',
  AFTER_REQUESTED: 'after_requested',
} as const;
export type AttentionKind = (typeof ATTENTION)[keyof typeof ATTENTION];

export const PO_TRACKER_TAB = {
  ALL: 'all',
  UPDATED: 'updated',
  LATE: 'late',
  THIS_WEEK: 'this_week',
  PARTLY_PLANNED: 'partly_planned',
  NO_DATE: 'no_date',
} as const;
export type PoTrackerTab = (typeof PO_TRACKER_TAB)[keyof typeof PO_TRACKER_TAB];

export const SHIPMENT_STATUS = { PLANNED: 'planned', SHIPPED: 'shipped' } as const;
export type ShipmentStatus = (typeof SHIPMENT_STATUS)[keyof typeof SHIPMENT_STATUS];

export const DELIVERY_REQUEST_STATUS = { OPEN: 'open', ANSWERED: 'answered', WITHDRAWN: 'withdrawn' } as const;
export type DeliveryRequestStatus = (typeof DELIVERY_REQUEST_STATUS)[keyof typeof DELIVERY_REQUEST_STATUS];

export const MOVE_REASON = {
  WAITING_FOR_MATERIALS: 'waiting_for_materials',
  PRODUCTION_CAPACITY: 'production_capacity',
  QUALITY_REWORK: 'quality_rework',
  OTHER: 'other',
} as const;
export type MoveReason = (typeof MOVE_REASON)[keyof typeof MOVE_REASON];

export const UNMATCHED_REASON = { NO_PO: 'no_po', PO_NOT_ON_EVENT: 'po_not_on_event', NO_LINE: 'no_line', NO_QUANTITY: 'no_quantity' } as const;
export type UnmatchedReason = (typeof UNMATCHED_REASON)[keyof typeof UNMATCHED_REASON];

export interface PoAttention {
  kind: AttentionKind;
  pieces: number | null;
  days: number | null;
}

export interface PoTrackerRow {
  poNumber: string;
  orderDate: string | null;
  status: PoStatus;
  attention: PoAttention | null;
  ordered: number;
  shipped: number;
  toPlan: number;
  nextShipDate: string | null;
  lastShipDate: string | null;
  lastShippedOn: string | null;
  openRequest: Pick<DeliveryRequest, 'id' | 'shipBy' | 'neededBy' | 'note' | 'requestedBy' | 'requestedAt'> | null;
  updated: boolean;
  // The PO's lines have no ids yet, so the quantities are unknown (0) until the next sync.
  linesSyncing: boolean;
}

// Every open delivery request of the vendor, whatever the tab, search or page; by ship-by, then PO.
export type OpenRequestItem = NonNullable<PoTrackerRow['openRequest']> & { poNumber: string; toPlan: number };

export interface PoTrackerList {
  rows: PoTrackerRow[];
  openRequests: OpenRequestItem[];
  // The vendor's transit days, for ship-by dates and the header.
  transitDays: number;
  total: number;
  page: number;
  pageSize: number;
  tabCounts: Record<PoTrackerTab, number>;
  syncedAt: string | null;
  syncError: string | null;
}

export interface PoTrackerQuery {
  vendorId: string;
  tab: PoTrackerTab;
  search?: string;
  page: number;
}

export interface PoLineQuantities {
  odooLineId: number;
  lineNo: number;
  partNumber: string | null;
  description: string;
  ordered: number;
  shipped: number;
  planned: number;
  toPlan: number;
}

export interface ShipmentLine {
  odooLineId: number;
  lineNo: number;
  partNumber: string | null;
  quantity: number;
}

export interface Shipment {
  id: string;
  poNumber: string;
  status: ShipmentStatus;
  plannedDate: string;
  firstPlannedDate: string;
  requestedShipBy: string | null;
  shippedOn: string | null;
  shippingEventId: string | null;
  note: string | null;
  lines: ShipmentLine[];
  updatedAt: string;
  updatedBy: string;
}

export interface PackingListSummary {
  id: string;
  version: number;
  fileName: string;
  note: string | null;
  uploadedBy: string;
  uploadedAt: string;
}

export interface PackingListRow {
  rowNumber: number;
  poNumber: string | null;
  partNumber: string | null;
  quantity: number | null;
  odooLineId: number | null;
  unmatchedReason: UnmatchedReason | null;
}

export interface PackingList extends PackingListSummary {
  rows: PackingListRow[];
}

export interface ShippingEvent {
  id: string;
  shippedOn: string;
  forwarder: string;
  hawb: string | null;
  // Distinct and sorted: every PO with a shipment on this event.
  poNumbers: string[];
  createdBy: string;
  createdAt: string;
  packingLists: PackingListSummary[];
}

export interface DeliveryRequestAnswer {
  shipmentId: string;
  plannedDate: string;
  pieces: number;
}

export interface DeliveryRequest {
  id: string;
  poNumber: string;
  neededBy: string;
  transitDays: number;
  shipBy: string;
  note: string | null;
  status: DeliveryRequestStatus;
  requestedBy: string;
  requestedAt: string;
  answeredBy: string | null;
  answeredAt: string | null;
  answerNote: string | null;
  answer: DeliveryRequestAnswer[] | null;
  withdrawnBy: string | null;
  withdrawnAt: string | null;
}

export interface PoDetail {
  poNumber: string;
  orderDate: string | null;
  // No line table until the next sync when the snapshot row predates the line-id reload.
  linesSyncing: boolean;
  lines: PoLineQuantities[];
  shipments: Shipment[];
  shippingEvents: ShippingEvent[];
  openRequest: DeliveryRequest | null;
  summary: PoTrackerRow;
}

export interface TrackerUpdates {
  seenAt: string;
  events: PoActivityEvent[];
}

export interface ShipmentLineInput {
  odooLineId: number;
  quantity: number;
}

export interface PlanShipmentBody {
  vendorId: string;
  plannedDate: string;
  note?: string;
  lines: ShipmentLineInput[];
}

export interface EditShipmentBody {
  plannedDate?: string;
  moveReason?: MoveReason;
  moveReasonText?: string;
  // null clears the note; absent leaves it alone.
  note?: string | null;
  lines?: ShipmentLineInput[];
}

export interface MarkShippedBody {
  vendorId: string;
  shippedOn: string;
  forwarder: string;
  hawb?: string;
  shipmentIds: string[];
}

export interface RequestDeliveryBody {
  vendorId: string;
  neededBy: string;
  note?: string;
}

export interface AnswerRequestBody {
  cards: { plannedDate: string; lines: ShipmentLineInput[] }[];
  note?: string;
}

export const IMPORT_OUTCOME = {
  SKIPPED_UNKNOWN_PO: 'skipped_unknown_po',
  SKIPPED_HAS_SHIPMENTS: 'skipped_has_shipments',
  NEEDS_DATE: 'needs_date',
  NEEDS_LINES: 'needs_lines',
  OVER_ORDER: 'over_order',
  SHIPPED_MISSING: 'shipped_missing',
  RESOLVED: 'resolved',
} as const;
export type ImportOutcome = (typeof IMPORT_OUTCOME)[keyof typeof IMPORT_OUTCOME];

export const IMPORT_SHIPMENT_KIND = { PLANNED: 'planned', SHIPPED: 'shipped' } as const;
export type ImportShipmentKind = (typeof IMPORT_SHIPMENT_KIND)[keyof typeof IMPORT_SHIPMENT_KIND];

export interface ImportLine {
  odooLineId: number;
  lineNo: number;
  partNumber: string | null;
  quantity: number;
}

export interface ImportCandidate {
  odooLineId: number;
  lineNo: number;
  partNumber: string | null;
  description: string;
  ordered: number;
  // Tracker room (ordered minus tracker shipments) and pieces already received, both before this import's takes.
  free: number;
  received: number;
}

export interface ImportShipment {
  kind: ImportShipmentKind;
  // Null on a needs_date row: the lines resolved, the partner types the date.
  date: string | null;
  forwarder: string | null;
  hawb: string | null;
  lines: ImportLine[];
}

export interface ImportPreviewRow {
  // Unique per preview row: "518", or "518/P13403WP" for one PO of a cell that named several.
  rowKey: string;
  // The sheet row; what the push sends.
  rowNumber: number;
  // The original PO cell of a split row, null otherwise.
  splitFrom: string | null;
  // The original HAWB/BOL cell of a row split out of a cell naming several waybills ("518#2"), null otherwise.
  splitWaybills: string | null;
  poNumber: string;
  classification: string;
  quantity: number | null;
  // The sheet's text: the ISO date when the cell parsed as one.
  etd: string;
  atd: string;
  forwarder: string | null;
  hawb: string | null;
  // The cell's words around the waybill and the ATD packing note; the shipment's note unless the partner edits it.
  note: string | null;
  // Shipped when the sheet has an ATD date or the lines are already received; null for a row skipped before line matching.
  kind: ImportShipmentKind | null;
  outcome: ImportOutcome;
  reason: string | null;
  checkSplit: boolean;
  shipment: ImportShipment | null;
  candidates: ImportCandidate[] | null;
}

export interface ImportPreview {
  rows: ImportPreviewRow[];
  counts: { resolved: number; needsInput: number; skipped: number };
}

export interface ImportPushShipment {
  rowNumber: number;
  rowKey: string;
  poNumber: string;
  kind: ImportShipmentKind;
  date: string;
  forwarder: string | null;
  hawb: string | null;
  note: string | null;
  lines: Pick<ImportLine, 'odooLineId' | 'quantity'>[];
}

export interface ImportPushBody {
  vendorId: string;
  shipments: ImportPushShipment[];
}

export interface ImportResult {
  written: { poNumber: string; shipments: number }[];
  failed: { poNumber: string; reason: string }[];
}
